import { useEffect, useState, useMemo } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/hooks/useAuth";
import { useBranchContext } from "@/contexts/BranchContext";
import { useBranches } from "@/hooks/useBranches";
import { ShoppingCart, Check } from "lucide-react";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "@/components/ui/command";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { NoMatchingRecords } from "@/components/NoMatchingRecords";
import { formatInZambia } from "@/lib/zambia-time";

interface InventoryItem {
  id: string;
  name: string;
  current_stock: number;
}

interface SaleRow {
  id: string;
  quantity: number;
  created_at: string;
  branch_id?: string | null;
  inventory: { name: string } | null;
}

export default function SalesPage() {
  const [inventory, setInventory] = useState<InventoryItem[]>([]);
  const [selectedItem, setSelectedItem] = useState<InventoryItem | null>(null);
  const [quantity, setQuantity] = useState(1);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const { toast } = useToast();
  const { user, role, businessId, isFounder } = useAuth();

  const [sales, setSales] = useState<SaleRow[]>([]);
  const { selectedBranchId } = useBranchContext();
  const { branches } = useBranches();

  useEffect(() => {
    if (!businessId) return;

    const inventoryQuery = supabase
      .from("inventory")
      .select("id, name, current_stock")
      .order("name")
      .eq("business_id", businessId);
    inventoryQuery.then(({ data }) => {
      if (data) setInventory(data);
    });

    if (role === "admin" || isFounder) {
      let salesQuery = supabase
        .from("sales")
        .select("id, quantity, created_at, branch_id, inventory(name)")
        .order("created_at", { ascending: false })
        .limit(50)
        .eq("business_id", businessId);
      if (selectedBranchId !== "all") salesQuery = salesQuery.eq("branch_id", selectedBranchId);
      salesQuery.then(({ data }) => {
        if (data) setSales(data as SaleRow[]);
      });
    }
  }, [role, businessId, selectedBranchId]);

  const handleSale = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedItem || !user || !businessId) return;
    setLoading(true);

    const branchId = selectedBranchId !== "all" ? selectedBranchId : branches[0]?.id ?? null;
    const { error } = await supabase.from("sales").insert({
      item_id: selectedItem.id,
      quantity,
      sold_by: user.id,
      business_id: businessId,
      payment_method: "Cash",
      branch_id: branchId,
    });

    if (error) {
      toast({ title: "Error", description: error.message, variant: "destructive" });
    } else {
      toast({ title: "Sale Logged", description: "Nexus has updated your inventory levels." });
      setSelectedItem(null);
      setQuantity(1);
      const inventoryQuery = supabase
        .from("inventory")
        .select("id, name, current_stock")
        .order("name")
        .eq("business_id", businessId);
      const { data } = await inventoryQuery;
      if (data) setInventory(data);
    }
    setLoading(false);
  };

  const filteredSales = useMemo(() => {
    if (selectedBranchId === "all") return sales;
    return sales.filter((s) => s.branch_id === selectedBranchId);
  }, [sales, selectedBranchId]);

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-display font-bold">
          {role === "admin" ? "Sales Log" : "New Sale"}
        </h2>
        <p className="text-sm text-muted-foreground">Record transactions</p>
      </div>

      <Card className="border border-border max-w-lg">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-base">
            <ShoppingCart className="h-4 w-4 text-primary" /> Record Sale
          </CardTitle>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSale} className="space-y-4">
            <div className="space-y-2">
              <Label>Select Item</Label>
              <Popover open={open} onOpenChange={setOpen}>
                <PopoverTrigger asChild>
                  <Button variant="outline" className="w-full justify-start font-normal">
                    {selectedItem ? selectedItem.name : "Search items..."}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-full p-0" align="start">
                  <Command>
                    <CommandInput placeholder="Search items..." />
                    <CommandList>
                      <CommandEmpty>No items found.</CommandEmpty>
                      <CommandGroup>
                        {inventory.map((item) => (
                          <CommandItem
                            key={item.id}
                            onSelect={() => { setSelectedItem(item); setOpen(false); }}
                          >
                            <Check className={`mr-2 h-4 w-4 ${selectedItem?.id === item.id ? "opacity-100" : "opacity-0"}`} />
                            {item.name}
                            <span className="ml-auto text-xs text-muted-foreground">Stock: {item.current_stock}</span>
                          </CommandItem>
                        ))}
                      </CommandGroup>
                    </CommandList>
                  </Command>
                </PopoverContent>
              </Popover>
            </div>
            <div className="space-y-2">
              <Label>Quantity</Label>
              <Input
                type="number"
                min={1}
                max={selectedItem?.current_stock || 999}
                value={quantity}
                onChange={(e) => setQuantity(+e.target.value)}
                required
              />
            </div>
            <Button
              type="submit"
              className="w-full bg-primary text-primary-foreground hover:bg-primary/90 font-semibold"
              disabled={!selectedItem || loading}
            >
              {loading ? "Logging..." : "Log Sale"}
            </Button>
          </form>
        </CardContent>
      </Card>

      {role === "admin" && (
        <Card className="border border-border">
          <CardHeader>
            <CardTitle className="text-base">Recent Sales</CardTitle>
          </CardHeader>
          <CardContent>
            {filteredSales.length === 0 ? (
              sales.length === 0 ? (
                <p className="text-muted-foreground text-sm py-4 text-center">
                  No sales have been recorded yet.
                </p>
              ) : (
                <NoMatchingRecords />
              )
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-border">
                      <th className="text-left py-3 px-2 font-semibold">Item</th>
                      <th className="text-right py-3 px-2 font-semibold">Qty</th>
                      <th className="text-right py-3 px-2 font-semibold">Date</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredSales.map((sale) => (
                      <tr key={sale.id} className="border-b border-border/50">
                        <td className="py-3 px-2">{sale.inventory?.name || "Unknown"}</td>
                        <td className="py-3 px-2 text-right font-bold">{sale.quantity}</td>
                        <td className="py-3 px-2 text-right text-muted-foreground">
                          {formatInZambia(sale.created_at)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
}
